/**
 * One QA or environment-setup run's whole deadline. Thirty minutes until
 * 2026-10-09; since then 90, because a time limit exists only to catch a
 * catastrophic stop, never to be why a slow install fails a good run. The
 * Temporal activities that carry a run sit five minutes above it, and a human
 * sign-in session's deadline (`qa_interactive_sessions`) is bounded by it.
 */
export const QA_RUN_DEADLINE_MS = 90 * 60 * 1000;
export const DEFAULT_QA_COMMAND_TIMEOUT_MS = 10 * 60 * 1000;
export const DEFAULT_QA_READINESS_TIMEOUT_MS = 10 * 60 * 1000;
export const DEFAULT_FLETCHER_STEPS = 200;
export const MAX_FLETCHER_STEPS = 1000;
export const MAX_QA_SCREENSHOT_COUNT = 2000;
export const MAX_QA_SCREENSHOT_BYTES = 1024 * 1024 * 1024;
export const DEFAULT_QA_SCREENSHOTS = {
    maxCount: 2000,
    maxBytes: 512 * 1024 * 1024,
};
export const MAX_QA_RECORDING_SECONDS = QA_RUN_DEADLINE_MS / 1000;
export const MAX_QA_RECORDING_BYTES = 100 * 1024 * 1024;
export const DEFAULT_QA_RECORDING = {
    maxSeconds: 600,
    maxBytes: 32 * 1024 * 1024,
};
export const MAX_QA_EVIDENCE_RECORDS = 4096;
export function isQaEvidenceLimitsValid(config) {
    const screenshots = config.screenshots ?? DEFAULT_QA_SCREENSHOTS;
    const recording = config.recording ?? DEFAULT_QA_RECORDING;
    return (Number.isInteger(screenshots.maxCount) &&
        screenshots.maxCount >= 1 &&
        screenshots.maxCount <= MAX_QA_SCREENSHOT_COUNT &&
        Number.isInteger(screenshots.maxBytes) &&
        screenshots.maxBytes >= 1024 * 1024 &&
        screenshots.maxBytes <= MAX_QA_SCREENSHOT_BYTES &&
        Number.isInteger(recording.maxSeconds) &&
        recording.maxSeconds >= 60 &&
        recording.maxSeconds <= MAX_QA_RECORDING_SECONDS &&
        Number.isInteger(recording.maxBytes) &&
        recording.maxBytes >= 8 * 1024 * 1024 &&
        recording.maxBytes <= MAX_QA_RECORDING_BYTES);
}
function isQaTimeoutValid(value) {
    return (value === undefined || (Number.isInteger(value) && value > 0 && value <= QA_RUN_DEADLINE_MS));
}
export const isQaReadinessTimeoutValid = isQaTimeoutValid;
export const isQaCommandTimeoutValid = isQaTimeoutValid;
/** An AWS region as Cognito names one, e.g. `us-east-1`. */
export const COGNITO_REGION = /^[a-z]{2}(-[a-z]+)+-\d$/;
/**
 * The pool's own regional API endpoint. Both the sandbox's egress allowlist and
 * the test browser's route filter are built from the environment's approved
 * origins, so this has to be one of them for the application to refresh its own
 * session. Returns null rather than throwing: callers are validators and UI.
 */
export function cognitoIdpOrigin(region) {
    return COGNITO_REGION.test(region) ? `https://cognito-idp.${region}.amazonaws.com` : null;
}
/** A step's time as a person reads it: `41 s`, `11 m 20 s`, `2 h 5 m`. */
export function qaStepDuration(ms) {
    const seconds = Math.max(0, Math.round(ms / 1000));
    if (seconds < 60)
        return `${seconds} s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60)
        return seconds % 60 ? `${minutes} m ${seconds % 60} s` : `${minutes} m`;
    const hours = Math.floor(minutes / 60);
    return minutes % 60 ? `${hours} h ${minutes % 60} m` : `${hours} h`;
}
/**
 * The sentence a run's failure opens with when a step failed: "Step 3,
 * `composer install`, line 14, failed in 41 s." The step counts from one;
 * the line part is there only when the step has a line, the time only when
 * it has one.
 */
export function qaStepFailureSentence(step) {
    return [
        `Step ${step.index + 1}`,
        `\`${step.label}\``,
        ...(step.line ? [`line ${step.line}`] : []),
        step.durationMs === undefined ? "failed." : `failed in ${qaStepDuration(step.durationMs)}.`,
    ].join(", ");
}
/**
 * The step whose failure ended a run: the furthest step it reached, when that
 * one failed. A declared tool that did not install never ends a run, so its
 * failure is passed over.
 */
export function qaEndingFailedStep(steps) {
    const reached = (steps ?? []).filter((step) => step.state !== "pending" &&
        step.state !== "skipped" &&
        !(step.kind === "tool" && step.state === "failed"));
    const last = reached.at(-1);
    return last?.state === "failed" ? last : undefined;
}
/** How a step of the tour of what changed ended, in a reviewer's words: the PR comment and the case say the same. */
export const QA_CHANGE_STEP_WORDS = {
    passed: "held",
    failed: "did not hold",
    not_checked: "shown",
    skipped: "not run",
};
/** "1:05" for 65 seconds: where a step starts in the tour's video. */
export function qaVideoTime(seconds) {
    const whole = Math.max(0, Math.floor(seconds));
    return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}
//# sourceMappingURL=qa.js.map